import React, { useMemo, useState, useEffect, useRef } from "react";
import * as d3 from "d3";
import "./Treemap.css"; // Ensure this CSS file exists and is imported

import { createSearchParams, useNavigate, useSearchParams } from "react-router-dom";

const Treemap = ({ width, height, data, time }) => {
  const [tooltip, setTooltip] = useState({
    visible: false,
    data: null,
    current: null,
    x: 0,
    y: 0,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const svgRef = useRef(null);

  const hierarchy = useMemo(() => {
    return d3
      .hierarchy(data.data)
      .sum((d) => Math.abs(d.value || 0)) // Sum absolute values
      .sort((a, b) => b.value - a.value);
  }, [data.data]);

  const firstLevelGroups = hierarchy?.children?.map((child) => child.data.name);
  const secondLevelGroups = hierarchy?.children?.flatMap(
    (child) => child.children?.map((subChild) => subChild.data.name) || []
  );

  const colorScaleFirstLevel = d3
    .scaleOrdinal()
    .domain(firstLevelGroups || [])
    .range(d3.schemeCategory10);
  const colorScaleSecondLevel = d3
    .scaleOrdinal()
    .domain(secondLevelGroups || [])
    .range(d3.schemeAccent);

  const root = useMemo(() => {
    const treeGenerator = d3
      .treemap()
      .size([width, height])
      .paddingInner(10) // Increased inner padding
      .paddingOuter(10) // Increased outer padding
      .paddingTop(30); // Increased top padding
    return treeGenerator(hierarchy);
  }, [hierarchy, width, height]);

  const handleNodeMouseOver = (event, node) => {
    const { clientX, clientY } = event;

    if (node.depth === 1) {
      setTooltip({
        visible: true,
        data: node.data,
        current: null,
        x: clientX,
        y: clientY,
      });
    } else if (node.depth === 2) {
      setTooltip({
        visible: true,
        data: node.parent.data,
        current: node.data,
        x: clientX,
        y: clientY,
      });
    }
  };

  const handleNodeMouseOut = () => {
    setTooltip({ visible: false, data: null, x: 0, y: 0 });
  };

  const handleNodeClick = (event, node) => {
    searchParams.append('category', node?.data?.name);
    searchParams.append('name', data.name)
    navigate({
      pathname: "/treemap-chart",
      search: createSearchParams(searchParams).toString(),
    }, {replace: false});
  }

  const calculatePercentageDiffColor = (value) => {
    const absValue = Math.abs(value); // Get absolute value
    if (absValue >= 3) return value > 0 ? "#238823" : "#ad2929";
    // Dark green for +3% or more, dark red for -3% or more
    else if (absValue >= 2) return value > 0 ? "#6cbf6c" : "#d9534f";
    // Medium green/red for +2% to +2.99% / -2% to -2.99%
    else if (absValue >= 1) return value > 0 ? "#2F9E4F" : "#BF4045";
    // Light green/red for +1% to +1.99% / -1% to -1.99%
    else return value >= 0 ? "#35764E" : "#8B444E"; // Very light green for +0.01% to +0.99%, very light red for -0.01% to -0.99%, gray for no change
  };

  const calculateFontSize = (
    text,
    maxWidth,
    maxHeight,
    minSize = 2,
    maxSize = 12
  ) => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    let fontSize = binarySearch(minSize, maxSize, (size) => {
      context.font = `${size}px Arial`;
      return context.measureText(text).width <= maxWidth && size <= maxHeight;
    });

    return fontSize;

    function binarySearch(low, high, condition) {
      while (low < high) {
        const mid = Math.floor((low + high) / 2);
        if (condition(mid)) {
          low = mid + 1;
        } else {
          high = mid;
        }
      }
      return low - 1;
    }
  };

  const nodeShapes = root
    .descendants()
    .filter((d) => d.depth === 1)
    .map((node, i) => {
      const fillColor = colorScaleFirstLevel(node.data.name);
      const maxWidth = node.x1 - node.x0 - 20; // Adjusted padding for text
      const nodeHeight = node.y1 - node.y0;

      const fontSize = calculateFontSize(node.data.name, maxWidth, nodeHeight);

      return (
        <g key={`node-${i}`}>
          <rect
            x={node.x0}
            y={node.y0}
            width={node.x1 - node.x0}
            height={nodeHeight}
            stroke="white" // Changed stroke to white
            fill={"black"}
            className="opacity-80 hover:opacity-100"
          />
          {maxWidth > 30 && (
            <text
              onMouseOver={(e) => handleNodeMouseOver(e, node)}
              onMouseOut={handleNodeMouseOut}
              onClick={(e) => handleNodeClick(e,node)}
              x={node.x0 + 10} // Increased padding for text
              y={node.y0 + 15} // Adjusted y position for text
              fontSize={fontSize}
              textAnchor="start"
              alignmentBaseline="hanging"
              fill="white" // Changed text color to white
              className="font-semibold"
            >
              {node.data.name}
            </text>
          )}
        </g>
      );
    });

  const leafShapes = root.leaves().map((leaf, i) => {
    const parentName = leaf.parent?.data.name;
    const fillColor = colorScaleSecondLevel(parentName);
    const leafWidth = leaf.x1 - leaf.x0;
    const leafHeight = leaf.y1 - leaf.y0;
    const maxWidth = leafWidth - 10; // Adjusted padding for text
    const maxHeight = leafHeight - 10;

    // Calculate percentage difference
    const value = leaf.data.diff[time];
    // Determine fill color based on percentage difference
    const fill = calculatePercentageDiffColor(value);

    const fontSize = calculateFontSize(
      leaf.data.name,
      maxWidth,
      leafHeight / 2
    );

    return (
      <g
        key={`leaf-${i}`}
        onMouseOver={(e) => handleNodeMouseOver(e, leaf)}
        onMouseOut={handleNodeMouseOut}
      >
        <rect
          x={leaf.x0}
          y={leaf.y0}
          width={leafWidth}
          height={leafHeight}
          stroke="white" // Changed stroke to white
          fill={fill} // Dynamic fill color based on percentage difference
          className="opacity-80 hover:opacity-100"
        />
        {maxWidth > 20 && maxHeight > 10 && (
          <>
            <text
              x={leaf.x0 + leafWidth / 2}
              y={leaf.y0 + leafHeight / 2 - 4} // Adjusted y position for text
              fontSize={fontSize}
              textAnchor="middle"
              alignmentBaseline="central"
              fill="white" // Changed text color to white
              className="font-bold"
            >
              {leaf.data.name}
            </text>
            <text
              x={leaf.x0 + leafWidth / 2}
              y={leaf.y0 + leafHeight / 2 + 10} // Adjusted y position for text
              fontSize={10} // Fixed font size for diff text
              textAnchor="middle"
              alignmentBaseline="central"
              fill="white" // Changed text color to white
              className="font-light"
            >
              {leaf.data.diff[time] >= 0
                ? `+${leaf.data.diff[time].toFixed(2)}%`
                : `${leaf.data.diff[time].toFixed(2)}%`}
            </text>
          </>
        )}
      </g>
    );
  });

  useEffect(() => {
    const svg = d3.select(svgRef.current);

    const handleMouseMove = (event) => {
      const { clientX, clientY } = event;
      const tooltipElement = document.querySelector(".tooltip");
      if (tooltipElement) {
        const tooltipWidth = tooltipElement.offsetWidth;
        const tooltipHeight = tooltipElement.offsetHeight;
        let x = clientX + 15;
        let y = clientY + 15;

        if (x + tooltipWidth > window.innerWidth) {
          x = clientX - tooltipWidth - 15;
        }

        if (y + tooltipHeight > window.innerHeight) {
          y = y + tooltip;
        }

        tooltipElement.style.left = `${x}px`;
        tooltipElement.style.top = `${y}px`;
      }
    };

    svg.on("mousemove", handleMouseMove);

    return () => {
      svg.on("mousemove", null);
    };
  }, []);

  // const tooltipElement =

  return (
    <div>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{ backgroundColor: "black" }}
        id="treemap"
      >
        {nodeShapes}
        {leafShapes}
      </svg>
      <div
        className="tooltip"
        style={{
          opacity: tooltip?.visible ? 1 : 0,
          left: tooltip.x,
          top: tooltip.y,
          minWidth: "150px",
          maxHeight: "500px",
          overflow: "hidden",
        }}
      >
        <div>
          <strong>{tooltip?.data?.name}</strong>
        </div>
        {tooltip.current && (
          <div style={{ marginTop: "5px" }}>
            <span style={{fontWeight: "bold",}}>{tooltip.current?.name}</span>
            <span 
              style={{
                float: "right",
                fontWeight: "bold",
                color: calculatePercentageDiffColor(
                  tooltip.current?.diff[time]
                ),
              }}
            >
              {tooltip.current?.diff[time] >= 0
                ? `+${tooltip.current?.diff[time].toFixed(2)}%`
                : `${tooltip.current?.diff[time].toFixed(2)}%`}
            </span>
          </div>
        )}
        {tooltip?.data?.children.map((stock, index) => {
          if (tooltip?.current?.data?.node !== stock.name) {
            return (
              <div key={index} style={{ marginTop: "5px" }}>
                <span>{stock.name}</span>
                <span
                  style={{
                    float: "right",
                    color: calculatePercentageDiffColor(stock.diff[time]),
                  }}
                >
                  {stock.diff[time] >= 0
                    ? `+${stock.diff[time].toFixed(2)}%`
                    : `${stock.diff[time].toFixed(2)}%`}
                </span>
              </div>
            );
          }
        })}
      </div>
    </div>
  );
};

export default Treemap;
